/**
 * Measuring on the page: a distance (drag), a perimeter or an area (click
 * each corner; double-click or Enter to finish), and calibrating a scale
 * (drag along something of known length).
 *
 * The readout is live, at the page's scale: a drawing's own viewport where
 * the pointer is, else the scale set in Odie. Shift keeps lines level,
 * plumb or at 45 degrees.
 */

import { useEffect, useState } from 'react'
import type { PageScale } from '../api'
import { unrotatePoint } from './rotation'

export type MeasureTool = 'measure-distance' | 'measure-perimeter' | 'measure-area' | 'calibrate'

interface Props {
  tool: MeasureTool
  width: number             // layer size, CSS px
  height: number
  /** The page's /Rotate; the pointer arrives in screen axes. */
  rotation?: number
  scale: number             // CSS px per point
  originX: number
  originY: number
  pageHeight: number
  scales: PageScale[]
  onDone: (points: number[]) => void
}

type Pt = { x: number; y: number }

const snap = (from: Pt, to: Pt): Pt => {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const angle = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4)
  const length = Math.hypot(dx, dy)
  return { x: from.x + Math.cos(angle) * length, y: from.y + Math.sin(angle) * length }
}

function scaleAt(scales: PageScale[], x: number, y: number): PageScale | null {
  for (const s of scales) {
    if (s.source === 'odie') return s
    if (s.bbox) {
      const [x0, y0, x1, y1] = s.bbox
      if (x >= Math.min(x0, x1) && x <= Math.max(x0, x1) && y >= Math.min(y0, y1) && y <= Math.max(y0, y1)) return s
    }
  }
  return null
}

function quote(value: number, unit: string): string {
  const digits = value >= 1000 ? 0 : value >= 100 ? 1 : 2
  return `${value.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })} ${unit}`
}

export function MeasureLayer(props: Props) {
  const { tool, scale } = props
  const [points, setPoints] = useState<Pt[]>([])
  const [hover, setHover] = useState<Pt | null>(null)
  const [dragging, setDragging] = useState(false)
  const drag = tool === 'measure-distance' || tool === 'calibrate'
  const closed = tool === 'measure-area'

  const toUser = (p: Pt) => ({ x: props.originX + p.x / scale, y: props.originY + props.pageHeight - p.y / scale })
  const user = (list: Pt[]) => list.flatMap((p) => { const u = toUser(p); return [u.x, u.y] })

  useEffect(() => { setPoints([]); setHover(null) }, [tool])

  const finish = (list: Pt[]) => {
    const minimum = closed ? 3 : 2
    if (list.length >= minimum) props.onDone(user(list))
    setPoints([])
    setHover(null)
  }

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setPoints([]); setHover(null) }
      else if (event.key === 'Enter' && !drag) finish(points)
      else if (event.key === 'Backspace' && !drag && points.length) setPoints(points.slice(0, -1))
      else return
      event.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  const local = (event: React.PointerEvent | React.MouseEvent): Pt => {
    const rect = event.currentTarget.getBoundingClientRect()
    let p = unrotatePoint(props.rotation ?? 0, event.clientX - rect.left, event.clientY - rect.top,
      props.width, props.height)
    const last = drag ? points[0] : points[points.length - 1]
    if (event.shiftKey && last) p = snap(last, p)
    return p
  }

  // The readout, in real units when the page has a scale.
  const shown = drag ? (points.length && hover ? [points[0], hover] : [])
    : hover ? [...points, hover] : points
  let readout = ''
  if (shown.length >= 2) {
    const u = shown.map(toUser)
    const s = scaleAt(props.scales, u[0].x, u[0].y)
    let length = 0
    for (let i = 1; i < u.length; i++) length += Math.hypot(u[i].x - u[i - 1].x, u[i].y - u[i - 1].y)
    if (tool === 'measure-area' && u.length >= 3) {
      let area = 0
      for (let i = 0; i < u.length; i++) {
        const a = u[i]
        const b = u[(i + 1) % u.length]
        area += a.x * b.y - b.x * a.y
      }
      area = Math.abs(area) / 2
      readout = s ? quote(area * s.factor * s.factor, `sq ${s.unit}`) : quote(area / 5184, 'sq in on paper')
    } else {
      readout = s && tool !== 'calibrate' ? quote(length * s.factor, s.unit) : quote(length / 72, 'in on paper')
    }
  }

  const path = shown.map((p) => `${p.x},${p.y}`).join(' ')
  const tip = shown[shown.length - 1]
  return (
    <div
      className="page__layer measure-layer"
      data-tool={tool}
      onPointerDown={(event) => {
        if (!drag) return
        event.preventDefault()
        try {
          ;(event.target as Element).setPointerCapture?.(event.pointerId)
        } catch {
          // Not a live pointer; measuring still works without capture.
        }
        const p = local(event)
        setPoints([p])
        setHover(p)
        setDragging(true)
      }}
      onPointerMove={(event) => {
        if (drag && !dragging) return
        setHover(local(event))
      }}
      onPointerUp={(event) => {
        if (!drag || !dragging) return
        setDragging(false)
        const end = local(event)
        if (points[0] && Math.hypot(end.x - points[0].x, end.y - points[0].y) > 4) finish([points[0], end])
        else { setPoints([]); setHover(null) }
      }}
      onClick={(event) => {
        if (drag) return
        event.stopPropagation()
        setPoints([...points, local(event)])
      }}
      onDoubleClick={(event) => {
        if (drag) return
        event.stopPropagation()
        // The double-click's two clicks each added the same corner.
        finish(points.slice(0, -1).length >= (closed ? 3 : 2) ? points.slice(0, -1) : points)
      }}
    >
      <svg className="measure-preview" width={props.width} height={props.height}>
        {shown.length >= 2 && (closed && shown.length >= 3
          ? <polygon points={path} />
          : <polyline points={path} />)}
        {shown.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={3} />)}
      </svg>
      {readout && tip && (
        <div className="measure-readout" style={{ left: tip.x + 12, top: tip.y + 12 }}>{readout}</div>
      )}
    </div>
  )
}
